import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { darkTheme, lightTheme } from '../../theme/theme';
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { PRODUCTS } from '../../utils/urlConfig';
import LinqOneQR from '../../assets/qr-verifications/linqone/linqoneqr-success_qr.png';
import LinqOneexpiredQR from '../../assets/qr-verifications/linqone/linqone-expiredqr.png';
import LinqOneSuspectedQR from '../../assets/qr-verifications/linqone/linqone_suspected_qr.png';
//import NeuhausQR from '../../assets/qr-verifications/chocolate/chocolate-1.png';
//import ChocolateexpiredQR from '../../assets/qr-verifications/chocolate/chocolate-expiredQr.png';
//import ChocolateSuspectedQR from '../../assets/qr-verifications/chocolate/chocolate-suspectedQr.png';
import ChocolateFictionalSuccessQR from '../../assets/qr-verifications/chocolate-fictional/chocolate-fictional-successQR.png';
import ChocolateFictionalExpiredQR from '../../assets/qr-verifications/chocolate-fictional/chocolate-fictional-expiredQR.png';
import ChocolateFictionalSuspectedQR from '../../assets/qr-verifications/chocolate-fictional/chocolate-fictional-suspectedQR.png';
import useTheme from '../../hooks/useTheme';
import TopBar from '../../layouts/TopBar';

const VerificationChecker = () => {
    const navigate = useNavigate();
    const { productId: urlProductId, serialNumber: urlSerialNumber } = useParams();
    const [isDark, setIsDark] = useTheme();
    const theme = isDark ? darkTheme : lightTheme;
    
    // State for each product's active tab
    const [linqOneTab, setLinqOneTab] = useState('verified');
    const [chocolateTab, setChocolateTab] = useState('verified');
    
    // QR code images for each product and state
    const linqOneQrImages = {
        verified: LinqOneQR,
        expired: LinqOneexpiredQR,
        suspected: LinqOneSuspectedQR
    };
    
    const chocolateQrImages = {
        verified: ChocolateFictionalSuccessQR,
        expired: ChocolateFictionalExpiredQR,
        suspected: ChocolateFictionalSuspectedQR
    };

    return (
        <div className="h-screen flex flex-col" style={{ backgroundColor: theme.background1 }}>
            {/* Header */}
            <TopBar
                theme={theme}
                isDark={isDark}
                setIsDark={setIsDark}
                title="Verification Checker"
                onBackClick={() => {
                    const backPath = urlProductId && urlSerialNumber 
                        ? `/01/${urlProductId}/21/${urlSerialNumber}/verification`
                        : '/verification';
                    navigate(backPath, { state: { isDark } });
                }}
                backgroundColor={theme.background1}
            />

            {/* product tabs */}
            <div className="flex-1 overflow-y-auto px-6 pt-20 pb-6 mb-8">
                {/* Linq One Product Section */}
                <ProductSection
                    productName={PRODUCTS.linqOne.displayName}
                    activeTab={linqOneTab}
                    setActiveTab={setLinqOneTab}
                    qrImages={linqOneQrImages}
                    theme={theme}
                    productKey="linqOne"
                />

                {/* Spacing between sections */}
                <div className="h-8"></div>

                {/* Chocolate Product Section */}
                <ProductSection
                    productName={PRODUCTS.chocolateFictional.displayName}
                    activeTab={chocolateTab}
                    setActiveTab={setChocolateTab}
                    qrImages={chocolateQrImages}
                    theme={theme}
                    productKey="chocolateFictional"
                />
            </div>   
        </div>
    );
}

// Product Section Component
const ProductSection = ({ productName, activeTab, setActiveTab, qrImages, theme, productKey }) => {
    const tabs = [
        { id: 'verified', label: 'Valid', icon: CheckCircle, color: '#10B981' },
        { id: 'expired', label: 'Expired', icon: XCircle, color: '#F59E0B' },
        { id: 'suspected', label: 'Suspected', icon: AlertTriangle, color: '#EF4444' }
    ];

    // Get GTIN and Serial Number from product config
    const productConfig = PRODUCTS[productKey];
    const gtin = productConfig?.gtin || 'N/A';
    const serialNumber = productConfig?.qrCodes?.[activeTab]?.serialNumber || productConfig?.serialNumber || 'N/A';

    return (
        <div 
            className="rounded-lg p-4"
            style={{ backgroundColor: theme.background2, borderColor: theme.stroke, borderWidth: 1 }}
        >
            {/* Product Name */}
            <h2 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
                {productName}
            </h2>

            {/* GTIN and Serial Number Display */}
            <div className="flex flex-col sm:flex-row gap-3 px-3 py-2 rounded-lg mb-4"
                style={{ backgroundColor: theme.background1, borderColor: theme.stroke, borderWidth: 1 }}>
                {/* GTIN Number */}
                <div className="flex flex-col flex-1">
                    <p className="text-xs font-medium mb-1" style={{ color: theme.gray }}>GTIN Number</p>
                    <p className="text-sm font-bold tracking-wide" style={{ color: theme.text }}>
                        {gtin}
                    </p>
                </div>

                {/* Divider - vertical on large screens, horizontal on small */}
                <div className="hidden sm:block w-px self-stretch" style={{ backgroundColor: theme.stroke }} />
                <div className="block sm:hidden h-px w-full" style={{ backgroundColor: theme.stroke }} />

                {/* Serial Number */}
                <div className="flex flex-col flex-1">
                    <p className="text-xs font-medium mb-1" style={{ color: theme.gray }}>Serial Number</p>
                    <p className="text-sm font-bold tracking-wide" style={{ color: theme.text }}>
                        {serialNumber}
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className="flex-1 py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center transition-all"
                            style={{
                                backgroundColor: isActive ? tab.color : theme.background1,
                                color: isActive ? '#ffffff' : theme.text,
                                borderColor: isActive ? tab.color : theme.stroke,
                                borderWidth: 1
                            }}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* QR Code Display */}
            <div className="w-full flex items-center justify-center">
                <div className="bg-white rounded-lg p-4 w-48 h-48 flex items-center justify-center">
                    <img 
                        src={qrImages[activeTab]} 
                        alt={`${productName} QR Code - ${activeTab}`} 
                        className="w-full h-full object-contain" 
                    />
                </div>
            </div>
        </div>
    );
};

export default VerificationChecker;